/**
 * gift-code.js
 *
 * Code-triggered free gift on the cart. Hooks into the theme's existing
 * cart-update pubsub + section-rendering pattern (FoxTheme.pubsub +
 * `cart:grouped-sections`). No full-page reloads.
 *
 * Components:
 *   <gift-code-form> — the input + Apply button (cart drawer + cart page)
 *   GiftCodeReconciler — singleton that subscribes to cartUpdate and removes
 *                       the gift line when invariants are broken
 *
 * Config: window.__giftCodeConfig (server-rendered by snippets/gift-code-config.liquid)
 */
(function () {
  if (window.__giftCodeAttached) return;
  window.__giftCodeAttached = true;

  function getConfig() {
    return (
      window.__giftCodeConfig || {
        code: 'REPOTFREE',
        variantId: 0,
        productId: 0,
        minCents: 900,
        eligibleIds: [],
        collectionTitle: 'Summer Refresh Event',
        giftPropertyKey: '_gift_code',
        messages: {},
      }
    );
  }

  function eligibleSet() {
    const c = getConfig();
    return new Set(c.eligibleIds || []);
  }

  // ---------------- email-link marker (localStorage gating) ----------------
  // The auto-add ONLY fires when this marker is set. The marker is set in
  // exactly two ways:
  //   1. The customer arrives via a URL containing ?gift=REPOTFREE (the email
  //      link). The JS reads the param, stores the marker, and silently sets
  //      Shopify's discount cookie.
  //   2. The customer manually types REPOTFREE into the cart form.
  // The marker is cleared when the customer manually removes the gift.

  const MARKER_KEY = 'giftCodeMarker';

  function hasMarker() {
    try {
      const c = getConfig();
      return localStorage.getItem(MARKER_KEY) === c.code;
    } catch (e) {
      return false;
    }
  }

  function setMarker() {
    try {
      const c = getConfig();
      localStorage.setItem(MARKER_KEY, c.code);
    } catch (e) {}
  }

  function clearMarker() {
    try {
      localStorage.removeItem(MARKER_KEY);
    } catch (e) {}
  }

  function captureUrlMarker() {
    try {
      const c = getConfig();
      const params = new URLSearchParams(window.location.search);
      const giftParam = (params.get('gift') || '').toUpperCase();
      if (!giftParam || giftParam !== c.code) return;

      // Set the localStorage marker.
      setMarker();

      // Silently set Shopify's discount cookie so checkout honors the code.
      fetch('/discount/' + encodeURIComponent(c.code), {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'manual',
      }).catch(function () {});

      // Strip the param from the URL so it doesn't get bookmarked / shared.
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('gift');
        window.history.replaceState({}, '', url.toString());
      } catch (e) {}
    } catch (e) {}
  }

  // ---------------- helpers ----------------

  function bundleSectionsToRender() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  }

  function fetchCart() {
    if (window.FoxTheme && FoxTheme.routes && FoxTheme.utils) {
      return fetch(FoxTheme.routes.cart_url, FoxTheme.utils.fetchConfig()).then(function (r) {
        return r.json();
      });
    }
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function findGiftLines(cart) {
    const c = getConfig();
    const key = c.giftPropertyKey;
    return (cart.items || []).filter(function (item) {
      return item.product_id === c.productId || (item.properties && item.properties[key] === c.code);
    });
  }

  function isCodeApplied(cart) {
    const c = getConfig();
    const codes = cart.discount_codes || [];
    return codes.some(function (d) {
      return (d.code || '').toUpperCase() === c.code;
    });
  }

  function qualifyingCents(cart) {
    const c = getConfig();
    const ids = eligibleSet();
    const giftKey = c.giftPropertyKey;
    return (cart.items || [])
      .filter(function (item) {
        if (item.properties && item.properties[giftKey] === c.code) return false;
        if (item.product_id === c.productId) return false;
        return ids.has(item.product_id);
      })
      .reduce(function (sum, item) {
        return sum + (item.original_line_price || item.line_price || 0);
      }, 0);
  }

  function publishCartUpdate(cartJson) {
    if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
    }
  }

  // Mirrors the theme's CartDiscount.handleFormSubmit pattern.
  async function applyDiscountAndGetSections() {
    const c = getConfig();
    const sections = bundleSectionsToRender();
    const config = FoxTheme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('discount', c.code);
    formData.append('sections', sections);
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    return fetch(FoxTheme.routes.cart_update_url, config).then(function (r) {
      return r.json();
    });
  }

  async function clearDiscount(remainingCodes) {
    const sections = bundleSectionsToRender();
    const config = FoxTheme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('discount', (remainingCodes || []).join(','));
    formData.append('sections', sections);
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    return fetch(FoxTheme.routes.cart_update_url, config).then(function (r) {
      return r.json();
    });
  }

  async function addGiftLine() {
    const c = getConfig();
    const body = JSON.stringify({
      items: [
        {
          id: c.variantId,
          quantity: 1,
          properties: (function () {
            const p = {};
            p[c.giftPropertyKey] = c.code;
            return p;
          })(),
        },
      ],
    });
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body,
    }).then(function (r) {
      return r.json();
    });
  }

  async function removeLine(lineKey) {
    const sections = bundleSectionsToRender();
    return fetch(FoxTheme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
        sections: sections,
        sections_url: window.location.pathname,
      }),
    }).then(function (r) {
      return r.json();
    });
  }

  async function changeLineQuantity(lineKey, qty) {
    const sections = bundleSectionsToRender();
    return fetch(FoxTheme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity: qty,
        sections: sections,
        sections_url: window.location.pathname,
      }),
    }).then(function (r) {
      return r.json();
    });
  }

  function formatMoney(cents) {
    if (window.FoxTheme && FoxTheme.Currency && FoxTheme.Currency.formatMoney) {
      return FoxTheme.Currency.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // ---------------- <gift-code-form> ----------------

  class GiftCodeForm extends HTMLElement {
    constructor() {
      super();
      this.form = null;
      this.input = null;
      this.submitBtn = null;
      this.messageEl = null;
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="gift_code"]');
      this.submitBtn = this.querySelector('button[type="submit"]');
      this.messageEl = this.querySelector('.gift-code-form__message');
      if (!this.form) return;
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }

    setLoading(state) {
      if (!this.submitBtn) return;
      if (state) {
        this.submitBtn.classList.add('btn--loading');
        this.submitBtn.setAttribute('aria-disabled', 'true');
      } else {
        this.submitBtn.classList.remove('btn--loading');
        this.submitBtn.removeAttribute('aria-disabled');
      }
    }

    showMessage(text, kind) {
      if (!this.messageEl) return;
      this.messageEl.classList.remove('!hidden', 'is-success', 'is-error');
      this.messageEl.classList.add(kind === 'success' ? 'is-success' : 'is-error');
      this.messageEl.innerText = text;
    }

    hideMessage() {
      if (!this.messageEl) return;
      this.messageEl.classList.add('!hidden');
      this.messageEl.innerText = '';
    }

    async handleSubmit(event) {
      event.preventDefault();
      const c = getConfig();
      const msg = c.messages || {};
      const entered = (this.input.value || '').trim().toUpperCase();

      this.hideMessage();

      if (!entered) {
        return;
      }
      if (entered !== c.code) {
        this.showMessage(msg.wrongCode || "That code isn't valid here.", 'error');
        return;
      }

      this.setLoading(true);
      try {
        const cart = await fetchCart();
        const existing = findGiftLines(cart);
        if (existing.length > 0) {
          this.showMessage(msg.alreadyApplied || 'Already applied.', 'success');
          this.input.value = '';
          // Make sure code cookie is set even if user re-applies
          const parsedState = await applyDiscountAndGetSections();
          await this._publishWithSections(parsedState);
          return;
        }

        const qualifying = qualifyingCents(cart);
        if (qualifying < c.minCents) {
          const need = (c.minCents - qualifying) / 100;
          const tmpl = msg.belowMinimum || 'Add $%need more from %collection to claim your free gift.';
          this.showMessage(
            tmpl.replace('%need', need.toFixed(2)).replace('%collection', c.collectionTitle || ''),
            'error'
          );
          return;
        }

        // Add gift line, then apply discount with sections (combined repaint).
        GiftCodeReconciler.pause();
        await addGiftLine();
        const parsedState = await applyDiscountAndGetSections();
        await this._publishWithSections(parsedState);
        // Customer opted in manually → set the marker so subsequent reloads
        // also auto-add (until they manually remove).
        setMarker();
        this.showMessage(msg.success || 'Free gift added!', 'success');
        this.input.value = '';
      } catch (err) {
        console.error('[gift-code] apply error', err);
        this.showMessage(msg.generic || 'Something went wrong. Please try again.', 'error');
      } finally {
        this.setLoading(false);
        GiftCodeReconciler.resume();
      }
    }

    async _publishWithSections(parsedState) {
      const cartJson = await fetchCart();
      cartJson.sections = parsedState.sections;
      publishCartUpdate(cartJson);
    }
  }

  // ---------------- GiftCodeReconciler ----------------

  const GiftCodeReconciler = (function () {
    let paused = false;
    let processing = false;
    let userRemoved = false;

    function pause() { paused = true; }
    function resume() { paused = false; }
    function markUserRemoved() { userRemoved = true; }

    async function reconcile(cart) {
      if (paused || processing) return;
      if (!cart || !cart.items) return;

      const c = getConfig();
      const giftLines = findGiftLines(cart);
      const codeApplied = isCodeApplied(cart);
      const qualifying = qualifyingCents(cart);
      const meetsThreshold = qualifying >= c.minCents;

      // ---- No-gift branch (Option D auto-add OR user-removed cleanup) ----
      if (giftLines.length === 0) {
        // If the user just removed the gift line, clear the discount code AND
        // the email-link marker so we don't immediately re-add it.
        if (userRemoved) {
          userRemoved = false;
          clearMarker();
          if (codeApplied) {
            processing = true;
            try {
              const remaining = (cart.discount_codes || [])
                .map(function (d) { return d.code; })
                .filter(function (code) { return (code || '').toUpperCase() !== c.code; });
              const cleared = await clearDiscount(remaining);
              const cartJson = await fetchCart();
              cartJson.sections = cleared.sections;
              publishCartUpdate(cartJson);
            } catch (err) {
              console.error('[gift-code] post-remove cleanup error', err);
            } finally {
              processing = false;
            }
          }
          return;
        }

        // Auto-add: email-link marker is set + code is present + eligibility
        // met + no gift yet → add it. Without the marker, never auto-add — this
        // is what enforces email-only gating.
        if (!hasMarker()) return;
        if (!codeApplied || !meetsThreshold) return;

        processing = true;
        try {
          await addGiftLine();
          const parsedState = await applyDiscountAndGetSections();
          const cartJson = await fetchCart();
          cartJson.sections = parsedState.sections;
          publishCartUpdate(cartJson);
        } catch (err) {
          console.error('[gift-code] auto-add error', err);
        } finally {
          processing = false;
        }
        return;
      }

      // ---- Maintenance branch (existing logic) ----
      // Determine the canonical gift line (first one).
      const primary = giftLines[0];
      const extras = giftLines.slice(1);

      processing = true;
      try {
        // 1. Remove any duplicate gift lines (only one allowed).
        for (let i = 0; i < extras.length; i++) {
          await removeLine(extras[i].key);
        }

        // 2. Code missing or threshold not met → remove the gift entirely.
        if (!codeApplied || !meetsThreshold) {
          const removeResp = await removeLine(primary.key);
          // Also clear the REPOTFREE code from the cart so checkout is clean.
          if (codeApplied) {
            const remaining = (cart.discount_codes || [])
              .map(function (d) { return d.code; })
              .filter(function (code) { return (code || '').toUpperCase() !== c.code; });
            const cleared = await clearDiscount(remaining);
            const cartJson = await fetchCart();
            cartJson.sections = cleared.sections;
            publishCartUpdate(cartJson);
          } else {
            publishCartUpdate(removeResp);
          }
          return;
        }

        // 3. Quantity > 1 → reduce to 1.
        if (primary.quantity > 1) {
          const changed = await changeLineQuantity(primary.key, 1);
          publishCartUpdate(changed);
          return;
        }

        // 4. If we removed extras above, repaint.
        if (extras.length > 0) {
          const cartJson = await fetchCart();
          // We didn't capture sections from the per-extra calls; do one final sections pass.
          const sections = bundleSectionsToRender();
          if (sections.length > 0) {
            const formData = new FormData();
            // No-op update (just to fetch fresh sections).
            formData.append('sections', sections);
            formData.append('sections_url', window.location.pathname);
            const config = FoxTheme.utils.fetchConfig('javascript');
            config.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete config.headers['Content-Type'];
            config.body = formData;
            const refreshed = await fetch(FoxTheme.routes.cart_update_url, config).then(function (r) {
              return r.json();
            });
            cartJson.sections = refreshed.sections;
          }
          publishCartUpdate(cartJson);
        }
      } catch (err) {
        console.error('[gift-code] reconcile error', err);
      } finally {
        processing = false;
      }
    }

    function attach() {
      if (!window.FoxTheme || !FoxTheme.pubsub || !FoxTheme.pubsub.PUB_SUB_EVENTS) {
        return setTimeout(attach, 200);
      }
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (data) {
        const cart = data && data.cart ? data.cart : null;
        if (cart) {
          reconcile(cart);
        } else {
          fetchCart().then(reconcile);
        }
      });
      // Initial reconciliation in case the page loaded with a broken state.
      fetchCart().then(reconcile);
    }

    return { pause: pause, resume: resume, attach: attach, markUserRemoved: markUserRemoved };
  })();

  // ---- Detect manual removal of the gift line ----
  // Listen for clicks on the theme's remove buttons. If the click targets the
  // gift product's line, mark the next cartUpdate as a user-initiated removal
  // so the reconciler clears the discount code instead of re-adding the gift.
  document.addEventListener(
    'click',
    function (e) {
      const removeBtn =
        (e.target.closest && (e.target.closest('[is="cart-remove-item"]') || e.target.closest('cart-remove-item')));
      if (!removeBtn) return;

      const lineEl = removeBtn.closest('[data-variant-id]') || removeBtn.closest('li[id^="CartDrawer-Item"]') || removeBtn.closest('tr');
      if (!lineEl) return;

      const c = getConfig();
      const variantAttr = lineEl.getAttribute('data-variant-id');
      const variantId = variantAttr ? parseInt(variantAttr, 10) : NaN;
      if (variantId === c.variantId || variantId === c.productId) {
        GiftCodeReconciler.markUserRemoved();
      }
    },
    true
  );

  // ---------------- boot ----------------

  if (!customElements.get('gift-code-form')) {
    customElements.define('gift-code-form', GiftCodeForm);
  }

  function boot() {
    // Capture ?gift=REPOTFREE from the URL FIRST, before the reconciler runs.
    // This is what gates auto-add to email recipients only.
    captureUrlMarker();
    GiftCodeReconciler.attach();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
